/**
 * LIGHTING timelines (2026-10-27, DEC-323; OPBridge S340's ask in `OPApps/opbridge-opbuttons.md`, accepted the same
 * day). One format: an ordinary `Timeline` with `purpose: 'lights'`, holding light lanes only and no song. Our operator
 * wants them apart from songs in the Timelines section, made from a full timeline with one press, and set as
 * performance mode's default lights (OPBridge DEC-734) from here.
 *
 * Pure: the store wraps these (`store.ts`), the Timelines section and the editor read them.
 *
 * The marker is EXPLICIT and never derived from the lanes: a full timeline that happens to hold only light lanes is
 * not re-labelled behind the operator's back. The one automatic change goes the other way and keeps a promise to
 * OPBridge ("from us a `purpose: lights` timeline never carries a non-light lane"): a lighting timeline that gains a
 * lane that is not a light, or a song, becomes a full timeline (`keepLightingHonest`).
 */
import type { Timeline, TimelineLane } from '@opbuttons/schema';

/** A lane that only lights the rig: "All lights" (`dmx-rig`), one light (`dmx-fixture`) or drum lights (`instrument`). */
export function isLightLane(lane: Pick<TimelineLane, 'laneKind'>): boolean {
  return lane.laneKind === 'dmx-rig' || lane.laneKind === 'dmx-fixture' || lane.laneKind === 'instrument';
}

export function isLightingTimeline(t: Pick<Timeline, 'purpose'> | undefined | null): boolean {
  return !!t && t.purpose === 'lights';
}

/** Why a lighting timeline can no longer be one, or null while it still is (or was never one). */
export function lightingBroken(t: Timeline): string | null {
  if (!isLightingTimeline(t)) return null;
  if (t.form === 'chain') return 'it is a chain';
  if (t.testTrack) return 'it has a song';
  const other = t.lanes.find((l) => !isLightLane(l));
  return other ? `it has a lane that is not lights ("${other.name}")` : null;
}

/** Without the marker. Returns `t` itself when it carries none. */
export function withoutLighting(t: Timeline): Timeline {
  if (!('purpose' in t)) return t;
  const { purpose: _drop, ...rest } = t;
  return rest;
}

/** The library with every lighting timeline that stopped being one made a full timeline again, and which they were.
 *  Unchanged timelines are returned as the same objects, so nothing is re-stamped. */
export function keepLightingHonest(ts: readonly Timeline[]): { timelines: Timeline[]; demoted: Array<{ name: string; why: string }> } {
  const demoted: Array<{ name: string; why: string }> = [];
  const timelines = ts.map((t) => {
    const why = lightingBroken(t);
    if (!why) return t;
    demoted.push({ name: t.name, why });
    return withoutLighting(t);
  });
  return { timelines, demoted };
}

/** Whether "Copy as lighting timeline" has anything to copy: a timeline (not a chain, not already lighting) with at
 *  least one light lane. */
export function canCopyAsLighting(t: Timeline): boolean {
  return !isLightingTimeline(t) && t.form !== 'chain' && t.lanes.some(isLightLane);
}

/** "Copy as lighting timeline": a NEW timeline (new ids) with the light lanes only, the song left behind, everything
 *  that shapes how the lights play kept (sections, tempo, loop, lead-in, lights at start and end, drum looks, strobes).
 *  `newId` makes the ids, `now` stamps it. */
export function lightingCopyOf(t: Timeline, name: string, newId: () => string, now: string): Timeline {
  const { testTrack: _song, ...rest } = withoutLighting(t);
  return {
    ...rest,
    id: newId(),
    name,
    purpose: 'lights',
    sections: t.sections.map((s) => ({ ...s, id: newId() })),
    lanes: t.lanes.filter(isLightLane).map((l) => ({ ...l, id: newId(), steps: l.steps.map((s) => ({ ...s, id: newId() })) })),
    created: now,
    modified: now,
    origin: 'opbuttons',
  };
}

/** "<name> lights", or "<name> lights 2" and on when that is taken. */
export function lightingCopyName(base: string, taken: ReadonlySet<string>): string {
  const plain = `${base} lights`;
  if (!taken.has(plain)) return plain;
  let n = 2;
  while (taken.has(`${plain} ${n}`)) n++;
  return `${plain} ${n}`;
}
